import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { Link } from "react-router-dom";
import { disableMessagePush, enableMessagePush, prepareMessagePush, type PushPreparation } from "./messagePush.js";
import "./messageAlerts.css";

const setupKey = (accountId: string) => `wmf-message-alerts-setup-v1:${accountId}`;
function wasSetUp(accountId: string): boolean {
  try { return localStorage.getItem(setupKey(accountId)) === "complete"; }
  catch { return false; }
}

export function MessageAlerts({ accountId, invitation = false, onDismiss, onEnabled }: {
  accountId: string; invitation?: boolean; onDismiss?: () => void; onEnabled?: () => void;
}) {
  const [prepared, setPrepared] = useState<PushPreparation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setUp, setSetUp] = useState(() => wasSetUp(accountId));
  const [feedback, setFeedback] = useState("");
  const [nonce, setNonce] = useState(0);
  const rememberSetup = useCallback(() => {
    setSetUp(true);
    try { localStorage.setItem(setupKey(accountId), "complete"); }
    catch { /* The bell still works when browser preferences cannot be saved. */ }
  }, [accountId]);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(""), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);
  useEffect(() => {
    let live = true; setError(null);
    void prepareMessagePush(accountId).then(value => { if (live) {
      setPrepared(value); if (value.state === "on") { rememberSetup(); onEnabled?.(); }
    } })
      .catch(() => { if (live) setError("We couldn't check message alerts. Try again."); });
    return () => { live = false; };
  }, [accountId, nonce, onEnabled, rememberSetup]);
  async function toggle() {
    if (!prepared || !["on", "off"].includes(prepared.state) || busy || !("registration" in prepared)) return;
    setBusy(true); setError(null); setFeedback("");
    try {
      const enabling = prepared.state === "off";
      if (prepared.state === "on") await disableMessagePush(accountId, prepared);
      else await enableMessagePush(accountId, prepared);
      const next = await prepareMessagePush(accountId);
      setPrepared(next);
      if (next.state !== (enabling ? "on" : "off")) throw new Error("We couldn't confirm the change. Check again.");
      if (next.state === "on") { rememberSetup(); onEnabled?.(); }
      setFeedback(enabling ? "Notifications enabled on this device." : "Notifications disabled on this device.");
    } catch (cause) {
      setError(cause instanceof Error && cause.name === "NotAllowedError"
        ? "Notifications weren't allowed. You can enable them in your device's notification settings."
        : cause instanceof Error ? cause.message : "We couldn't change message alerts. Try again.");
    } finally { setBusy(false); }
  }
  if (invitation && (!prepared || prepared.state === "on")) return null;
  if (!prepared && !error || prepared?.state === "unsupported" || prepared?.state === "unconfigured") return null;
  const compact = !invitation && setUp && (prepared?.state === "on" || prepared?.state === "off");
  const enabled = prepared?.state === "on";
  const label = enabled ? "Turn off message alerts on this device" : "Turn on message alerts on this device";
  const copy = prepared?.state === "on" ? "On for this device, including when Hub is closed. Message contents stay private."
    : prepared?.state === "install" ? "On iPhone or iPad, use Share → Add to Home Screen, then open Hub from its icon to enable alerts."
    : prepared?.state === "blocked" ? "Notifications are blocked. Allow notifications for Hub in your device or browser settings, then check again."
    : prepared?.state === "update" ? "Update Hub and reopen it to enable message alerts."
    : "Get an alert on this device when a new message arrives, even when Hub is closed.";
  const controls = <>
    {prepared && (prepared.state === "on" || prepared.state === "off") && <button type="button"
      className={compact ? "quiet message-alert-bell" : "quiet"} disabled={busy}
      aria-label={compact ? "Message alerts on this device" : undefined}
      aria-pressed={compact ? enabled : undefined} aria-busy={busy || undefined}
      title={compact ? label : undefined} onClick={() => void toggle()}>
      {compact ? enabled ? <Bell size={20} aria-hidden="true" /> : <BellOff size={20} aria-hidden="true" />
        : busy ? "Updating…" : enabled ? "Turn off alerts" : "Enable message alerts"}
    </button>}
    {(error || prepared?.state === "blocked" || prepared?.state === "update") && <button type="button" className="quiet" disabled={busy} onClick={() => setNonce(value => value + 1)}>Check again</button>}
  </>;
  return <div className={compact ? "message-alert-toggle" : invitation ? "panel message-alert-prompt" : "inbox-message-alerts"} aria-label="Message alerts">
    {!compact && <div>{invitation ? <h2>Get new message alerts</h2> : <strong className="small">Message alerts</strong>}
      <p className="small muted">{copy}</p>
      {invitation && <p className="small muted">You choose whether to enable alerts on each device. You can change this later in Inbox.</p>}
    </div>}
    {invitation ? <div className="message-alert-prompt-actions">{controls}
      <Link to="/help/enable-message-alerts-as-a-tutor">How to set up alerts</Link>
      <button type="button" className="quiet" disabled={busy} onClick={onDismiss}>Not now</button>
    </div> : controls}
    <span className="message-alert-feedback" role="status" aria-live="polite" aria-atomic="true">{feedback}</span>
    {error && <p className="small error" role="alert">{error}</p>}
  </div>;
}
